import { formatMoney } from '@/lib/finance';
import { PAYMENT_METHODS, VAT_RATES, type DocTotals } from '@/lib/invoiceDoc';
import { Field, inputClass } from '@/pages/dashboard/finance/components/FinanceUI';

interface Props {
  totals: DocTotals;
  discount: number;
  onDiscount: (v: number) => void;
  taxRate: number;
  onTaxRate: (v: number) => void;
  paymentTerms: string;
  onPaymentTerms: (v: string) => void;
  paymentMethod: string;
  onPaymentMethod: (v: string) => void;
  mentions: string;
  onMentions: (v: string) => void;
  currency: string;
  isQuote: boolean;
  /** Le commerce est-il assujetti à la TVA (Paramètres → Fiscalité) ? */
  vatSubject: boolean;
}

// Bloc 5 — totaux, TVA et modalités de paiement.
export default function InvoiceTotalsBlock({
  totals,
  discount,
  onDiscount,
  taxRate,
  onTaxRate,
  paymentTerms,
  onPaymentTerms,
  paymentMethod,
  onPaymentMethod,
  mentions,
  onMentions,
  currency,
  isQuote,
  vatSubject,
}: Props) {
  const standardRates = VAT_RATES.map((r) => r.value);
  const hasCustomRate = !standardRates.includes(taxRate);

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-3">
        <i className="ri-money-dollar-circle-line text-primary-500"></i>
        Bloc 5 · Totaux & modalités de paiement
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="grid grid-cols-2 gap-x-3">
            <Field label="Remise globale (HT)">
              <input
                type="number" min="0" step="0.01" value={discount}
                onChange={(e) => onDiscount(parseFloat(e.target.value) || 0)}
                className={inputClass} placeholder="0.00"
              />
            </Field>
            <Field label="TVA applicable">
              {vatSubject ? (
                <select
                  value={String(taxRate)}
                  onChange={(e) => onTaxRate(parseFloat(e.target.value) || 0)}
                  className={`${inputClass} cursor-pointer`}
                >
                  {hasCustomRate && <option value={String(taxRate)}>{taxRate} % (personnalisé)</option>}
                  {VAT_RATES.map((r) => <option key={r.value} value={String(r.value)}>{r.label}</option>)}
                </select>
              ) : (
                <div className={`${inputClass} flex items-center gap-2 bg-background-100 text-foreground-600`}>
                  <i className="ri-lock-line text-foreground-400"></i>
                  <span className="text-xs">Non assujetti — TVA 0 %</span>
                </div>
              )}
            </Field>
          </div>

          {!vatSubject && (
            <p className="text-[11px] text-accent-700 bg-accent-50 rounded-md px-2.5 py-2 mb-2 flex items-start gap-1.5">
              <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
              Vous n’êtes pas assujetti à la TVA. Activez « Assujetti à la TVA » dans
              Paramètres facturation pour l’appliquer partout (factures, commandes, devis).
            </p>
          )}

          <Field label="Conditions et délais de paiement">
            <input
              value={paymentTerms}
              onChange={(e) => onPaymentTerms(e.target.value)}
              className={inputClass}
              placeholder="Ex : Paiement à 30 jours"
            />
          </Field>

          <Field label="Mode de paiement">
            <select value={paymentMethod} onChange={(e) => onPaymentMethod(e.target.value)} className={`${inputClass} cursor-pointer`}>
              <option value="">— Choisir —</option>
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>

          <Field label="Mentions particulières">
            <textarea
              value={mentions}
              onChange={(e) => onMentions(e.target.value)}
              rows={2}
              maxLength={500}
              className={inputClass}
              placeholder="Ex : Franchise de TVA, autoliquidation, attestation d’exonération…"
            />
          </Field>
        </div>

        <div className="bg-background-100 rounded-lg p-4 h-fit">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-foreground-600">
              <span>Total HT brut</span><span>{formatMoney(totals.gross, currency)}</span>
            </div>
            {totals.lineDiscounts > 0 && (
              <div className="flex justify-between text-foreground-600">
                <span>Remises sur lignes</span><span>− {formatMoney(totals.lineDiscounts, currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-foreground-700 font-medium">
              <span>Total HT</span><span>{formatMoney(totals.subtotal, currency)}</span>
            </div>
            {totals.globalDiscount > 0 && (
              <div className="flex justify-between text-foreground-600">
                <span>Remise globale</span><span>− {formatMoney(totals.globalDiscount, currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-foreground-600">
              <span>{totals.taxRate > 0 ? `TVA ${totals.taxRate} %` : 'TVA (exonéré)'}</span>
              <span>{formatMoney(totals.tax, currency)}</span>
            </div>
            <div className="flex justify-between font-bold text-foreground-950 border-t border-background-200/70 pt-2 mt-2 text-base">
              <span>{isQuote ? 'Total devis TTC' : 'Total TTC'}</span>
              <span>{formatMoney(totals.total, currency)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}